import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
    Alert,
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    Button,
    Stack,
    Chip,
    TextField,
    IconButton,
    Snackbar,
    Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PeopleIcon from "@mui/icons-material/People";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { API_URL, apiFetch } from "../config";
import SiteAnalyticsCard from "../components/SiteAnalyticsCard";
import StatCard from "../components/ui/StatCard";
import GlowCard from "../components/ui/GlowCard";

function addDays(d, n) {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
}

function monthNow() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const Dashboard = () => {
    const [tasks, setTasks] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [events, setEvents] = useState([]);
    const [financeSummary, setFinanceSummary] = useState({ income: 0, expense: 0, net: 0 });
    const [reminders, setReminders] = useState([]);
    const [newReminder, setNewReminder] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editDraft, setEditDraft] = useState("");
    const [reminderBusy, setReminderBusy] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const fetchReminders = useCallback(async () => {
        try {
            const res = await apiFetch(`${API_URL}/reminders`);
            const data = await res.json();
            if (res.ok && Array.isArray(data)) setReminders(data);
        } catch (e) {
            console.error(e);
        }
    }, []);

    useEffect(() => {
        const load = async () => {
            try {
                const [tRes, cRes, eRes, sRes] = await Promise.all([
                    apiFetch(`${API_URL}/tasks`),
                    apiFetch(`${API_URL}/customers`),
                    apiFetch(`${API_URL}/calendar-events`),
                    apiFetch(`${API_URL}/transactions/summary?month=${encodeURIComponent(monthNow())}`),
                ]);
                const [tData, cData, eData, sData] = await Promise.all([
                    tRes.json(),
                    cRes.json(),
                    eRes.json(),
                    sRes.json(),
                ]);
                if (tRes.ok && Array.isArray(tData)) setTasks(tData);
                if (cRes.ok && Array.isArray(cData)) setCustomers(cData);
                if (eRes.ok && Array.isArray(eData)) setEvents(eData);
                if (sRes.ok && sData) setFinanceSummary(sData);
            } catch (e) {
                console.error(e);
            }
        };
        load();
        fetchReminders();
    }, [fetchReminders]);

    const handleAddReminder = async () => {
        const content = newReminder.trim();
        if (!content) return;
        setReminderBusy(true);
        try {
            const res = await apiFetch(`${API_URL}/reminders`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ content }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Failed to add reminder");
            setNewReminder("");
            await fetchReminders();
            setSnackbar({ open: true, message: "Reminder added.", severity: "success" });
        } catch (e) {
            setSnackbar({ open: true, message: e.message || "Failed to add reminder.", severity: "error" });
        } finally {
            setReminderBusy(false);
        }
    };

    const startEdit = (item) => {
        setEditingId(item.id);
        setEditDraft(item.content);
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditDraft("");
    };

    const saveEdit = async () => {
        const content = editDraft.trim();
        if (!content || !editingId) return;
        setReminderBusy(true);
        try {
            const res = await apiFetch(`${API_URL}/reminders/${editingId}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ content }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Failed to update");
            cancelEdit();
            await fetchReminders();
            setSnackbar({ open: true, message: "Reminder updated.", severity: "success" });
        } catch (e) {
            setSnackbar({ open: true, message: e.message || "Failed to update.", severity: "error" });
        } finally {
            setReminderBusy(false);
        }
    };

    const deleteReminder = async (id) => {
        setReminderBusy(true);
        try {
            const res = await apiFetch(`${API_URL}/reminders/${id}`, { method: "DELETE" });
            if (!res.ok && res.status !== 204) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.error || "Failed to delete");
            }
            if (editingId === id) cancelEdit();
            await fetchReminders();
            setSnackbar({ open: true, message: "Reminder deleted.", severity: "success" });
        } catch (e) {
            setSnackbar({ open: true, message: e.message || "Failed to delete.", severity: "error" });
        } finally {
            setReminderBusy(false);
        }
    };

    const formatReminderWhen = (item) => {
        const raw = item.updated_at || item.created_at;
        if (!raw) return "";
        const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
        const d = new Date(normalized);
        return Number.isNaN(d.getTime()) ? raw : d.toLocaleString();
    };

    const today = useMemo(() => new Date(), []);
    const weekEnd = useMemo(() => addDays(today, 7), [today]);

    const upcomingTasks = useMemo(() => {
        return tasks.filter((task) => {
            if (!task.deadlineDate) return false;
            const deadline = new Date(task.deadlineDate);
            return deadline >= today && deadline <= weekEnd && task.status !== "complete";
        });
    }, [tasks, today, weekEnd]);

    const upcomingEvents = useMemo(() => {
        return events
            .filter((ev) => {
                const start = new Date(ev.start_at);
                return start >= today && start <= weekEnd;
            })
            .sort((a, b) => new Date(a.start_at) - new Date(b.start_at))
            .slice(0, 8);
    }, [events, today, weekEnd]);

    const taskCounts = useMemo(() => {
        const c = { toDo: 0, inProgress: 0, onHold: 0, complete: 0 };
        tasks.forEach((t) => {
            if (c[t.status] !== undefined) c[t.status] += 1;
        });
        return c;
    }, [tasks]);

    const fmt = (n) =>
        new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(n || 0);

    const activeTasks = taskCounts.toDo + taskCounts.inProgress + taskCounts.onHold;
    const greeting = useMemo(() => {
        const h = new Date().getHours();
        if (h < 12) return "Good morning";
        if (h < 17) return "Good afternoon";
        return "Good evening";
    }, []);

    return (
        <Box>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ sm: "center" }}
                justifyContent="space-between"
                spacing={2}
                sx={{ mb: 3 }}
            >
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {greeting}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                        {activeTasks} active task{activeTasks === 1 ? "" : "s"} · {customers.length} customers ·{" "}
                        {upcomingEvents.length} event{upcomingEvents.length === 1 ? "" : "s"} this week
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button component={RouterLink} to="/quotes" variant="contained" size="small">
                        New quote
                    </Button>
                    <Button component={RouterLink} to="/tasks" variant="outlined" size="small">
                        Task board
                    </Button>
                </Stack>
            </Stack>

            <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Tasks"
                        subtitle={`Active ${activeTasks} · Done ${taskCounts.complete}`}
                        icon={AssignmentIcon}
                        color="primary"
                        to="/tasks"
                        linkLabel="Open board"
                        LinkComponent={RouterLink}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Customers"
                        value={customers.length}
                        subtitle="CRM directory"
                        icon={PeopleIcon}
                        color="secondary"
                        to="/customers"
                        linkLabel="Directory"
                        LinkComponent={RouterLink}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Calendar"
                        subtitle={`${events.length} scheduled event${events.length === 1 ? "" : "s"}`}
                        icon={CalendarMonthIcon}
                        color="info"
                        to="/calendar"
                        linkLabel="Schedule"
                        LinkComponent={RouterLink}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="This month"
                        value={fmt(financeSummary.net)}
                        subtitle={`In ${fmt(financeSummary.income)} · Out ${fmt(financeSummary.expense)}`}
                        icon={AccountBalanceIcon}
                        color={financeSummary.net >= 0 ? "success" : "error"}
                        to="/finances"
                        linkLabel="Finances"
                        LinkComponent={RouterLink}
                    />
                </Grid>
            </Grid>

            <Box sx={{ mb: 3 }}>
                <SiteAnalyticsCard />
            </Box>

            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <GlowCard
                        title="Upcoming events"
                        subtitle="Next 7 days"
                        icon={CalendarMonthIcon}
                        action={
                            <Chip size="small" label={`${upcomingEvents.length} soon`} variant="outlined" color="primary" />
                        }
                    >
                        {upcomingEvents.length > 0 ? (
                            <List dense disablePadding>
                                {upcomingEvents.map((ev) => (
                                    <ListItem
                                        key={ev.id}
                                        disablePadding
                                        sx={{
                                            py: 1,
                                            px: 1.25,
                                            mb: 0.75,
                                            borderRadius: 2,
                                            bgcolor: "action.hover",
                                        }}
                                    >
                                        <ListItemText
                                            primary={ev.title}
                                            secondary={`${new Date(ev.start_at).toLocaleString()} · ${ev.event_type || "event"}`}
                                            slotProps={{ primary: { fontWeight: 600, fontSize: "0.9rem" } }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No events in the next week.
                            </Typography>
                        )}
                        <Button component={RouterLink} to="/calendar" size="small" sx={{ mt: 1.5 }}>
                            Full calendar
                        </Button>
                    </GlowCard>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <GlowCard
                        title="Task deadlines"
                        subtitle="Due within 7 days"
                        icon={AssignmentIcon}
                        action={
                            <Chip size="small" label={`${upcomingTasks.length} due`} variant="outlined" color="warning" />
                        }
                    >
                        {upcomingTasks.length > 0 ? (
                            <List dense disablePadding>
                                {upcomingTasks.map((task) => (
                                    <ListItem
                                        key={task.id}
                                        disablePadding
                                        sx={{
                                            py: 1,
                                            px: 1.25,
                                            mb: 0.75,
                                            borderRadius: 2,
                                            bgcolor: "action.hover",
                                        }}
                                    >
                                        <ListItemText
                                            primary={task.title}
                                            secondary={`Due ${new Date(task.deadlineDate).toLocaleDateString()} · ${task.status}`}
                                            slotProps={{ primary: { fontWeight: 600, fontSize: "0.9rem" } }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No open tasks due in the next 7 days.
                            </Typography>
                        )}
                        <Button component={RouterLink} to="/tasks" size="small" sx={{ mt: 1.5 }}>
                            Tasks
                        </Button>
                    </GlowCard>
                </Grid>
            </Grid>

            <Box sx={{ mt: 2.5 }}>
                <GlowCard
                    title="Reminders & notes"
                    subtitle="Quick capture — Ctrl+Enter to save"
                    icon={StickyNote2Icon}
                    action={<Chip size="small" label={`${reminders.length} saved`} variant="outlined" />}
                    hover={false}
                >
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
                        <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={6}
                            placeholder="Follow up with customer, order tooling, quote callback…"
                            value={newReminder}
                            onChange={(e) => setNewReminder(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleAddReminder();
                            }}
                            disabled={reminderBusy}
                        />
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddReminder}
                            disabled={reminderBusy || !newReminder.trim()}
                            sx={{ alignSelf: { sm: "flex-start" }, whiteSpace: "nowrap", minWidth: 120 }}
                        >
                            Add note
                        </Button>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                        Ctrl+Enter to save quickly. Stored in your local database — visible every time you open the dashboard.
                    </Typography>

                    <Divider sx={{ mb: 1.5 }} />

                    {reminders.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No reminders yet. Jot down anything you need to remember before it slips off the shop floor.
                        </Typography>
                    ) : (
                        <List dense disablePadding>
                            {reminders.map((item) => (
                                <ListItem
                                    key={item.id}
                                    alignItems="flex-start"
                                    sx={{
                                        py: 1.25,
                                        px: 1,
                                        mb: 1,
                                        borderRadius: 1,
                                        border: 1,
                                        borderColor: "divider",
                                        bgcolor: editingId === item.id ? "action.hover" : "transparent",
                                    }}
                                    secondaryAction={
                                        editingId === item.id ? (
                                            <Stack direction="row">
                                                <IconButton
                                                    edge="end"
                                                    aria-label="Save edit"
                                                    onClick={saveEdit}
                                                    disabled={reminderBusy || !editDraft.trim()}
                                                    size="small"
                                                    color="primary"
                                                >
                                                    <CheckIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton edge="end" aria-label="Cancel edit" onClick={cancelEdit} size="small">
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        ) : (
                                            <Stack direction="row">
                                                <IconButton
                                                    edge="end"
                                                    aria-label="Edit reminder"
                                                    onClick={() => startEdit(item)}
                                                    disabled={reminderBusy}
                                                    size="small"
                                                >
                                                    <EditOutlinedIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    edge="end"
                                                    aria-label="Delete reminder"
                                                    onClick={() => deleteReminder(item.id)}
                                                    disabled={reminderBusy}
                                                    size="small"
                                                    color="error"
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        )
                                    }
                                >
                                    {editingId === item.id ? (
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={2}
                                            value={editDraft}
                                            onChange={(e) => setEditDraft(e.target.value)}
                                            disabled={reminderBusy}
                                            size="small"
                                            sx={{ pr: 6 }}
                                        />
                                    ) : (
                                        <ListItemText
                                            primary={item.content}
                                            secondary={formatReminderWhen(item)}
                                            slotProps={{
                                                primary: { sx: { whiteSpace: "pre-wrap", pr: 6 }, variant: "body2" },
                                            }}
                                        />
                                    )}
                                </ListItem>
                            ))}
                        </List>
                    )}
                </GlowCard>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Dashboard;
